import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Box, Button, Dialog, DialogContent, DialogTitle,
  IconButton, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { usePageMeta } from "../../contexts/PageMetaContext";
import useApiCall from "../../hooks/useApiCall";
import LoadingSpinner from "../../components/LoadingSpinner";
import BottomActionBar from "../../components/BottomActionBar";
import JsonEditor from "../../components/JsonEditor";
import { getStringParamsFromObject } from "../../utils";
import { SLATE } from "../../constants/Colors";

const CONTROL_TYPE = "keyword_penalize_weights";
const GROUP = "ai_controls";

const BUTTON_SX = {
  textTransform: "none",
  fontSize: "0.875rem",
  fontWeight: 500,
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

const prettyJson = (val) => {
  try { return JSON.stringify(JSON.parse(val), null, 2); } catch { return val ?? ""; }
};

const compactJson = (val) => {
  try { return JSON.stringify(JSON.parse(val)); } catch { return null; }
};

export default function UrlWeightage() {
  const tokens = useSelector((state) => state?.tokens?.value);
  const currentApp = useSelector((state) => state?.apps?.selectedApp);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();

  const [value, setValue] = useState("");
  const [configId, setConfigId] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, env: "" });
  const originalRef = useRef(null);

  const openDialog = useCallback((env) => setDialog({ open: true, env }), []);

  useEffect(() => {
    const isNonDev = process.env.REACT_APP_CURRENT_ENVIRONMENT !== "development"; // eslint-disable-line no-undef
    setPageMeta({
      description: "Configure URL keyword penalization weights.",
      actions: isNonDev ? (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={() => openDialog("stage")}
            sx={{ ...BUTTON_SX, borderColor: SLATE[300], color: SLATE[700], "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50], boxShadow: "none" } }}>
            Push to Stage
          </Button>
          <Button variant="contained" onClick={() => openDialog("prod")} sx={BUTTON_SX}>
            Push to Prod
          </Button>
        </Box>
      ) : null,
    });
    return () => setPageMeta({});
  }, [openDialog]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentApp?.id) return;
    setLoading(true);
    const query = getStringParamsFromObject({ app_id: currentApp.id, types: CONTROL_TYPE });
    makeRequest(`/v1/controls/list_ai_controls?${query}`, { method: "GET", authTokens: tokens })
      .then((configs) => {
        if (Array.isArray(configs) && configs.length > 0) {
          const c = configs[0];
          const pretty = prettyJson(c.prompt_input_raw);
          originalRef.current = pretty;
          setValue(pretty);
          setConfigId(c.id ?? null);
        } else {
          originalRef.current = "";
          setValue("");
          setConfigId(null);
        }
        setIsDirty(false);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentApp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (newVal) => {
    setValue(newVal);
    setIsDirty(newVal !== originalRef.current);
  };

  const handleSave = async () => {
    const compact = compactJson(value);
    if (compact === null) return;
    const config = {
      display_name: "URL Weightage",
      internal_name: CONTROL_TYPE,
      prompt_input_raw: compact,
      ...(configId ? { id: configId } : {}),
    };
    setSaving(true);
    try {
      await makeRequest("/v1/controls/upsert_ai_controls", {
        method: "PUT",
        body: { controls: [config], app_id: currentApp?.id },
        authTokens: tokens,
      });
      const pretty = prettyJson(compact);
      originalRef.current = pretty;
      setValue(pretty);
      setIsDirty(false);
    } catch {
      // error toasted by useApiCall
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmPush = async () => {
    const env = dialog.env;
    setDialog({ open: false, env: "" });
    try {
      await makeRequest(
        `/v1/controls/push_to_another_environment?${getStringParamsFromObject({ app_id: currentApp?.id })}`,
        {
          method: "POST",
          body: { environment: env, internal_names: [CONTROL_TYPE], group: GROUP },
          authTokens: tokens,
        }
      );
    } catch {
      // error toasted by useApiCall
    }
  };

  if (loading) return <LoadingSpinner message="Loading URL Weightage..." subMessage="Fetching configuration" />;

  return (
    <Box sx={{ width: "100%", pb: 10 }}>
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${SLATE[200]}`,
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)",
        }}
      >
        <Box sx={{ px: 3, py: "11px", borderBottom: `1px solid ${SLATE[200]}`, bgcolor: SLATE[50] }}>
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: SLATE[900] }}>
            URL Weightage
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500], lineHeight: 1.5, mb: 2 }}>
            JSON configuration for keyword penalization weights applied to URLs.
          </Typography>
          <JsonEditor
            value={value}
            onChange={handleChange}
            height="420px"
          />
        </Box>
      </Box>

      <BottomActionBar
        disabledSave={!isDirty || saving}
        handleSave={handleSave}
        saving={saving}
      />

      {/* Push confirmation dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false, env: "" })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>
            Confirm Push
          </Typography>
          <IconButton size="small" onClick={() => setDialog({ open: false, env: "" })} sx={{ color: SLATE[400] }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.875rem", color: SLATE[600], mb: 3 }}>
            Are you sure you want to push to {dialog.env === "stage" ? "Stage" : "Production"}?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" onClick={() => setDialog({ open: false, env: "" })}
              sx={{ ...BUTTON_SX, borderColor: SLATE[300], color: SLATE[700], "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50], boxShadow: "none" } }}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleConfirmPush} sx={BUTTON_SX}>
              Push
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
